import { getCurrentLocale } from './formatting';
import { Invoice, LineItem } from './api';

// Convert a major-unit amount into integer cents.
export const toCents = (value: number): number => Math.round(value * 100);

// Convert integer cents back into a major-unit amount.
export const fromCents = (cents: number): number => cents / 100;

// Outstanding amount on an invoice.
export const invoiceBalance = (invoice: Invoice): number => invoice.amount - invoice.total_paid;

// Format cents as a localized currency string.
export const formatCurrency = (amount: number, currency: string) => {
  return new Intl.NumberFormat(getCurrentLocale(), {
    style: 'currency',
    currency: currency,
  }).format(fromCents(amount));
};

// Sum of the line item amounts (in cents).
export const calculateTotal = (lineItems: LineItem[]): number => {
  return lineItems.reduce((sum, item) => sum + item.amount, 0);
};

// Tax preview for the create form. The rate is applied per line item with
// Math.round, exactly as the backend does (invoices.service.ts:111-133), so the
// preview always matches the amount that gets persisted (fixed DEFECT-28).
export const calculateTaxBreakdown = (
  lineItems: LineItem[],
  pricesIncludeTax: boolean | undefined,
  ratePercentage?: number
): { subtotal: number; taxAmount: number; total: number } => {
  const lineItemsTotal = calculateTotal(lineItems);

  if (ratePercentage === undefined) {
    return { subtotal: lineItemsTotal, taxAmount: 0, total: lineItemsTotal };
  }

  const rateDecimal = parseFloat(ratePercentage.toString()) / 100;

  if (pricesIncludeTax) {
    // Post-tax: amounts include tax
    // per item: pre_tax = round(amount / (1 + rate)), tax = amount - pre_tax
    const preTaxAmount = lineItems.reduce(
      (sum, item) => sum + Math.round(item.amount / (1 + rateDecimal)),
      0
    );
    const taxAmount = lineItemsTotal - preTaxAmount;
    return { subtotal: preTaxAmount, taxAmount, total: lineItemsTotal };
  } else {
    // Pre-tax: amounts exclude tax
    // per item: tax = round(amount * rate)
    const taxAmount = lineItems.reduce(
      (sum, item) => sum + Math.round(item.amount * rateDecimal),
      0
    );
    return { subtotal: lineItemsTotal, taxAmount, total: lineItemsTotal + taxAmount };
  }
};
